import { without } from 'ramda';
import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useReducer,
} from 'react';
import { MediaManagerSDKFile } from '../../../../typings/media-manager-sdk';
import {
  FilesStateActionType,
  IFilesAction,
  IFilesContext,
} from './useFiles.types';

const FilesContext = createContext<IFilesContext>({
  files: [],
  activeFile: undefined,
  onFileAction: () => {},
});

const filesReducer = (
  state: {
    files: MediaManagerSDKFile[];
    activeFile: MediaManagerSDKFile | undefined;
  },
  action: IFilesAction,
) => {
  const {
    type,
    payload: { files = [] },
  } = action;

  switch (type) {
    case FilesStateActionType.Prepend:
      return { ...state, files: [...files, ...state.files] };

    case FilesStateActionType.Delete:
      return {
        ...state,
        files: without(files, state.files),
      };

    case FilesStateActionType.SetActive:
      return { ...state, activeFile: files[0] };

    default:
      return state;
  }
};

export const FilesProvider: FC<{ files: MediaManagerSDKFile[] }> = ({
  children,
  files = [],
}) => {
  const [state, dispatch] = useReducer(filesReducer, {
    files,
    activeFile: undefined,
  });

  const handleFileAction = useCallback(
    ({ type, payload }: IFilesAction) => {
      dispatch({
        type,
        payload,
      });
    },
    [dispatch],
  );

  return (
    <FilesContext.Provider
      value={{
        files: state.files,
        activeFile: state.activeFile,
        onFileAction: handleFileAction,
      }}
    >
      {children}
    </FilesContext.Provider>
  );
};

export const useGetFilesData = () => {
  const { files, activeFile } = useContext(FilesContext);
  return { files, activeFile };
};

export const useFilesAction = () => {
  const { onFileAction } = useContext(FilesContext);
  return { onFileAction };
};
